// 004 (FR-003, FR-035–FR-037, FR-053; P-PROF-3, BR-1): Delivery addresses. Each row shows its title,
// the Default badge and one line; Edit opens the form, Set as default and Delete (after
// confirmation) re-list from the server, which picks the new default. Phones: each address is one
// tappable row into its screen (where Set as default and Delete live); Add address is the last row.
import { useRouter } from "expo-router";
import { useState } from "react";
import { View } from "react-native";

import { isAppError } from "@/api/errors";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { useTheme } from "@/theme/useTheme";
import { ConfirmDialog } from "@/ui/ConfirmDialog";
import { PlusIcon } from "@/ui/icons/PlusIcon";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { ListRow } from "@/ui/ListRow";
import { Text } from "@/ui/Text";

import { PROFILE_COPY, profileErrorMessage } from "./copy";
import { Card, SectionHead, useProfileStyles } from "./ui";
import { useParentData } from "./useParentData";
import { type AddressVM, canAddAddress } from "./viewModels";

export function AddressesSection({
  items,
  online,
}: {
  items: readonly AddressVM[];
  online: boolean;
}) {
  const styles = useProfileStyles();
  const router = useRouter();
  const { actions } = useParentData();
  const [toDelete, setToDelete] = useState<AddressVM | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canAdd = canAddAddress(items.length);
  const phone = useChromeKind() === "mobile";
  const theme = useTheme();

  if (phone) {
    return (
      <Card testID="profile-addresses">
        <SectionHead
          title={PROFILE_COPY.addresses}
          subtitle={PROFILE_COPY.addressesSubtitle}
        />
        {items.length === 0 ? (
          <Text tone="muted">{PROFILE_COPY.addressesEmpty}</Text>
        ) : null}
        <View>
          {items.map((a) => (
            <ListRow
              key={a.id}
              testID={`address-${a.id}`}
              label={a.title}
              badge={
                a.isDefault ? (
                  <View style={styles.badge}>
                    <Text size="xs" weight="semiBold" style={styles.badgeText}>
                      {PROFILE_COPY.defaultBadge}
                    </Text>
                  </View>
                ) : null
              }
              detail={a.oneLine}
              accessibilityLabel={PROFILE_COPY.editAddress(a.title)}
              chevron
              disabled={!online}
              onPress={() => router.push(`/addresses/${a.id}`)}
            />
          ))}
          {canAdd ? (
            <ListRow
              testID="profile-add-address"
              leading={
                <View style={styles.avatar}>
                  <PlusIcon
                    color={theme.color.primaryText}
                    size={theme.size.icon.md}
                  />
                </View>
              }
              label={PROFILE_COPY.addAddress}
              tone="action"
              disabled={!online}
              onPress={() => router.push("/addresses/new")}
            />
          ) : (
            <Text tone="muted">{PROFILE_COPY.addressLimit}</Text>
          )}
        </View>
      </Card>
    );
  }

  async function run(work: () => Promise<unknown>) {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      if (isAppError(e))
        setError(profileErrorMessage(e, { action: "address" }).form ?? null);
    } finally {
      setPending(false);
    }
  }

  return (
    <Card testID="profile-addresses">
      <SectionHead
        title={PROFILE_COPY.addresses}
        subtitle={PROFILE_COPY.addressesSubtitle}
        action={
          canAdd ? (
            <LinkButton
              disabled={!online}
              onPress={() => router.push("/addresses/new")}
            >
              {PROFILE_COPY.addAddress}
            </LinkButton>
          ) : null
        }
      />
      {!canAdd ? <Text tone="muted">{PROFILE_COPY.addressLimit}</Text> : null}
      {items.length === 0 ? (
        <Text tone="muted">{PROFILE_COPY.addressesEmpty}</Text>
      ) : null}
      {error ? <InlineMessage tone="error">{error}</InlineMessage> : null}
      {items.map((a) => (
        <View key={a.id} style={styles.row} testID={`address-${a.id}`}>
          <View style={styles.rowText}>
            <View style={styles.actions}>
              <Text weight="semiBold">{a.title}</Text>
              {a.isDefault ? (
                <View style={styles.badge}>
                  <Text size="xs" weight="semiBold" style={styles.badgeText}>
                    {PROFILE_COPY.defaultBadge}
                  </Text>
                </View>
              ) : null}
            </View>
            <Text tone="muted">{a.oneLine}</Text>
          </View>
          <View style={styles.actions}>
            {!a.isDefault ? (
              <LinkButton
                accessibilityLabel={`${PROFILE_COPY.setDefault}: ${a.title}`}
                disabled={!online || pending}
                onPress={() => void run(() => actions.setDefaultAddress(a.id))}
              >
                {PROFILE_COPY.setDefault}
              </LinkButton>
            ) : null}
            <LinkButton
              accessibilityLabel={PROFILE_COPY.editAddress(a.title)}
              disabled={!online}
              onPress={() => router.push(`/addresses/${a.id}`)}
            >
              Edit
            </LinkButton>
            <LinkButton
              accessibilityLabel={PROFILE_COPY.deleteAddress(a.title)}
              disabled={!online || pending}
              onPress={() => setToDelete(a)}
            >
              {PROFILE_COPY.delete}
            </LinkButton>
          </View>
        </View>
      ))}
      <ConfirmDialog
        visible={toDelete !== null}
        title={PROFILE_COPY.deleteConfirm}
        message={toDelete?.oneLine}
        confirmLabel={PROFILE_COPY.delete}
        cancelLabel={PROFILE_COPY.cancel}
        pending={pending}
        onCancel={() => setToDelete(null)}
        onConfirm={() => {
          const target = toDelete;
          if (!target) return;
          void run(() => actions.deleteAddress(target.id)).then(() =>
            setToDelete(null),
          );
        }}
      />
    </Card>
  );
}
